if (!customElements.get('related-products-swatches')) {
  customElements.define('related-products-swatches', class RelatedProductsSwatches extends HTMLElement {
    debounce(fn, wait) {
      let timeout;
      return function () {
        clearTimeout(timeout);
        const context = this;
        const args = arguments;
        timeout = setTimeout(function () {
          fn.apply(context, args);
        }, wait);
      };
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.layout();
      this.bindHoverPreview();

      this.handleResize = this.debounce(() => this.layout(), 100);
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this);
      } else {
        window.addEventListener('resize', this.handleResize);
      }
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.handleResize) window.removeEventListener('resize', this.handleResize);
    }

    getHiddenLabels() {
      try {
        return JSON.parse(this.dataset.labels || '{}');
      } catch (error) {
        return {};
      }
    }

    layout() {
      const list = this.querySelector('.related-products-swatches__list');
      const more = this.querySelector('[data-related-products-more]');
      if (!list || !more) return;

      const items = Array.from(list.querySelectorAll('.related-products-swatches__item'));
      if (!items.length) return;

      items.forEach((item) => {
        item.hidden = false;
      });
      more.hidden = true;

      const containerWidth = this.clientWidth;
      const swatchWidth = items[0].offsetWidth;
      if (!containerWidth || !swatchWidth) return;

      const listStyles = window.getComputedStyle(list);
      const gap = parseFloat(listStyles.columnGap || listStyles.gap) || 0;

      const maxVisible = Math.floor((containerWidth + gap) / (swatchWidth + gap));
      if (maxVisible >= items.length) return;

      const visibleCount = Math.max(1, maxVisible - 1);
      const hiddenCount = items.length - visibleCount;

      for (let i = visibleCount; i < items.length; i++) {
        items[i].hidden = true;
      }

      const labels = this.getHiddenLabels();
      more.textContent = labels[hiddenCount] || `+${hiddenCount}`;
      more.hidden = false;
    }

    capturePreviewTargets(card) {
      return {
        card: card,
        mainImage: card.querySelector('.product-card__image .first-image'),
        vendorEl: card.querySelector('.product-card__type'),
        titleEl: card.querySelector('.product-card__name'),
        priceEl: card.querySelector('.product-card__price'),
        hideOnPreview: Array.from(card.querySelectorAll('.js-btn-quickview, .js-btn-wishlist')),
        original: null
      };
    }

    captureOriginal(targets) {
      targets.original = {
        imageSrc: targets.mainImage ? (targets.mainImage.currentSrc || targets.mainImage.src) : null,
        imageSrcset: targets.mainImage ? targets.mainImage.getAttribute('srcset') : null,
        vendorHtml: targets.vendorEl ? targets.vendorEl.innerHTML : null,
        vendorHref: targets.vendorEl ? targets.vendorEl.getAttribute('href') : null,
        titleText: targets.titleEl ? targets.titleEl.textContent : null,
        titleHref: targets.titleEl ? targets.titleEl.getAttribute('href') : null,
        priceHtml: targets.priceEl ? targets.priceEl.innerHTML : null
      };
    }

    showPreview(targets, swatch) {
      if (!targets.card.classList.contains('product-card--sibling-preview')) {
        this.captureOriginal(targets);
      }
      targets.card.classList.add('product-card--sibling-preview');

      if (targets.mainImage && swatch.dataset.image) {
        targets.mainImage.removeAttribute('srcset');
        targets.mainImage.src = swatch.dataset.image;
      }
      if (targets.vendorEl && swatch.dataset.vendor) {
        targets.vendorEl.textContent = swatch.dataset.vendor;
      }
      if (targets.vendorEl && swatch.dataset.vendorUrl) {
        targets.vendorEl.setAttribute('href', swatch.dataset.vendorUrl);
      }
      if (targets.titleEl && swatch.dataset.title) {
        targets.titleEl.textContent = swatch.dataset.title;
      }
      if (targets.titleEl && swatch.getAttribute('href')) {
        targets.titleEl.setAttribute('href', swatch.getAttribute('href'));
      }
      if (targets.priceEl && swatch.dataset.price) {
        targets.priceEl.innerHTML = swatch.dataset.price;
      }

      targets.hideOnPreview.forEach((el) => {
        el.style.setProperty('display', 'none', 'important');
      });
    }

    resetPreview(targets) {
      targets.card.classList.remove('product-card--sibling-preview');

      if (targets.original) {
        if (targets.mainImage && targets.original.imageSrc) {
          targets.mainImage.src = targets.original.imageSrc;
          if (targets.original.imageSrcset) {
            targets.mainImage.setAttribute('srcset', targets.original.imageSrcset);
          } else {
            targets.mainImage.removeAttribute('srcset');
          }
        }
        if (targets.vendorEl && targets.original.vendorHtml !== null) {
          targets.vendorEl.innerHTML = targets.original.vendorHtml;
        }
        if (targets.vendorEl && targets.original.vendorHref !== null) {
          targets.vendorEl.setAttribute('href', targets.original.vendorHref);
        }
        if (targets.titleEl && targets.original.titleText !== null) {
          targets.titleEl.textContent = targets.original.titleText;
        }
        if (targets.titleEl && targets.original.titleHref !== null) {
          targets.titleEl.setAttribute('href', targets.original.titleHref);
        }
        if (targets.priceEl && targets.original.priceHtml !== null) {
          targets.priceEl.innerHTML = targets.original.priceHtml;
        }
      }

      targets.hideOnPreview.forEach((el) => {
        el.style.removeProperty('display');
      });
    }

    bindHoverPreview() {
      const card = this.closest('.product-card');
      if (!card) return;

      const targets = this.capturePreviewTargets(card);
      const swatches = this.querySelectorAll('[data-related-products-swatch]');
      const hoverZone = this.closest('.product-card__info') || card;

      swatches.forEach((swatch) => {
        swatch.addEventListener('mouseenter', () => this.showPreview(targets, swatch));
        swatch.addEventListener('focus', () => this.showPreview(targets, swatch));
      });

      hoverZone.addEventListener('mouseleave', () => this.resetPreview(targets));
      hoverZone.addEventListener('focusout', (event) => {
        if (!hoverZone.contains(event.relatedTarget)) {
          this.resetPreview(targets);
        }
      });
    }
  });
}
